import * as React from 'react';
import {
  NinetailedProvider,
  useNinetailed,
  type NinetailedProviderInstantiationProps,
} from '@ninetailed/experience.js-react';
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights';
import { getRequestContext, historyEvent, HistoryEventType } from '@avensia/excite';
import { useGetPreviewPlugin } from './preview';
import { Ninetailed } from '@ninetailed/experience.js';
import { JsonObject, NINETAILED_ANONYMOUS_ID_COOKIE } from '@ninetailed/experience.js-shared';
import { NinetailedPlugin } from '@ninetailed/experience.js-plugin-analytics';
import { env } from 'hono/adapter';

declare global {
  interface Window {
    NT_VARIANTS: Record<string, number>;
    NT_API_KEY?: string;
    NT_ENV?: string;
  }
}

export function getExperienceWithSelectedVariantId(): Record<string, number> {
  if (__BROWSER__) {
    return window.NT_VARIANTS || {};
  }

  const c = getRequestContext().honoContext;
  return c.get('NT_VARIANTS') || {};
}

function getProfileIdFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === NINETAILED_ANONYMOUS_ID_COOKIE) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export let ninetailed: Ninetailed;

export async function ninetailedTrack(name: string, data: JsonObject) {
  if (__BROWSER__) {
    await ninetailed.track(name, JSON.parse(JSON.stringify(data)));
  }
}

export async function ninetailedIdentify(traits: JsonObject) {
  if (__BROWSER__) {
    const profileId = getProfileIdFromCookie();
    if (profileId) {
      await ninetailed.identify(profileId, traits);
    }
  }
}

function getNtConfig(): { clientId?: string; environment?: string } {
  if (__BROWSER__) {
    return {
      clientId: window.NT_API_KEY,
      environment: window.NT_ENV,
    };
  }

  const c = getRequestContext().honoContext;
  const { NINETAILED_API_KEY, NINETAILED_ENVIRONMENT } = env(c);
  return {
    clientId: NINETAILED_API_KEY,
    environment: NINETAILED_ENVIRONMENT,
  };
}
function useNinetailedPage() {
  React.useEffect(() => {
    if (ninetailed) {
      ninetailed.page();
    }

    const unsubscribe = historyEvent.on(event => {
      if (event.event === HistoryEventType.Push) {
        ninetailed.page();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [ninetailed]);
}

export const AvensiaNinetailedProvider = React.memo(function AvensiaNinetailedProvider(
  props: React.PropsWithChildren<Omit<NinetailedProviderInstantiationProps, 'clientId' | 'environment'>>,
) {
  const previewPlugin = useGetPreviewPlugin();
  const { children, plugins, ...resetProps } = props;
  const { clientId, environment } = getNtConfig();

  if (!ninetailed && clientId && environment) {
    const options = __BROWSER__
      ? {
          plugins: [new NinetailedInsightsPlugin(), previewPlugin, ...(plugins ?? [])].filter(
            Boolean,
          ) as NinetailedPlugin[],
          onInitProfileId: () => {
            const profileId = getProfileIdFromCookie();
            return profileId;
          },
          ...resetProps,
        }
      : {};

    ninetailed = new Ninetailed({ clientId, environment }, options);
  }

  useNinetailedPage();

  return <NinetailedProvider ninetailed={ninetailed}>{children}</NinetailedProvider>;
});

export * from './NinetailedExperience';
export * from './NinetailedContentList';
export * from './types';
export * from './utils';
